<script setup lang="ts">
import { reactive, ref, toRaw } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import DriverAutocompleteField from '@/components/fields/DriverAutocompleteField.vue';
import DateRangePickerField from '@/components/pickerFields/DateRangePickerField.vue';
import useForm from '@/composables/useForm';

export interface ValidData {
  dateRange: [Date, Date] | null;
  driver: number | null;
}

export interface InitialData {
  dateRange?: [Date, Date];
  driver?: number;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});

const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<ValidData>({
  dateRange: props.initialData.dateRange || null,
  driver: props.initialData.driver || null,
});

const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  const rawFormData = toRaw(formData);
  emit('submit', {
    dateRange: rawFormData.dateRange,
    driver: rawFormData.driver,
  });
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.smsHistorySearchForm"
    data-testid="smsHistorySearchForm"
    @submit.prevent="handleSubmit"
  >
    <DateRangePickerField
      v-model="formData.dateRange"
      size="lg"
      label="Date Range"
      :disabled="props.loading"
      :class="$style.field"
    />
    <DriverAutocompleteField
      v-model="formData.driver"
      size="lg"
      :disabled="props.loading"
      :class="[$style.field, $style.driverField]"
      @keydown.enter="handleSubmit"
    />
    <KBtn
      type="submit"
      size="lg"
      prepend-icon="mdi-magnify"
      :loading="props.loading"
      :class="$style.submit"
    >
      Search
    </KBtn>
  </v-form>
</template>

<style module lang="scss">
@use '@/styles/colors';

.smsHistorySearchForm {
  display: flex;
  align-items: flex-start;
}

.field {
  width: 320px;
  display: inline-block;
  vertical-align: top;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;
}

.field.driverField {
  width: 320px;
  flex: 0 0 auto;
}

.submit {
  margin-top: 0.5rem;
}
</style>
